/**
 * GridCrash — Daily Quests & Streaks
 *
 * Three quests per UTC day, chosen deterministically from the date (the
 * daily-challenge seed machinery), so every player sees the same board
 * of quests with zero server involvement. Completed quests pay Clash
 * Coins into the local wallet via a manual Claim.
 *
 * The streak counts consecutive UTC days with at least one finished
 * game (win or lose). Milestones auto-pay; every 7th day banks a
 * streak saver (max 2) that silently absorbs a single missed day.
 *
 * Anti-cheat status: local-trust, same tier as offline coin earnings —
 * everything funnels through the clamped migrate_local_wallet fold. A
 * server claim_quest RPC is the follow-up (see ROADMAP.md).
 *
 * Storage: localStorage with in-memory fallback (achievements.ts pattern)
 */

import { getDailySeed, seededRng } from "./daily.js";
import { creditBonus } from "./wallet.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface QuestGameResult {
  won: boolean;
  difficultyId: string;
  timeSecs: number;
  correctFlags: number;
  score: number;
  isDaily: boolean;
}

export interface QuestDef {
  id: string;
  label: string;
  target: number;
  reward: number;
  /** Progress contributed by one finished game. */
  progressFrom: (result: QuestGameResult) => number;
}

export interface QuestProgress {
  id: string;
  progress: number;
  claimed: boolean;
}

export interface StreakState {
  count: number;
  lastPlayedDate: string | null; // "YYYY-MM-DD" UTC
  savers: number;                // banked streak savers (max 2)
}

interface QuestsState {
  date: string;
  quests: QuestProgress[];
  streak: StreakState;
}

export interface QuestUpdate {
  /** Quests that crossed their target with this game. */
  justCompleted: QuestDef[];
  /** Streak milestone coins auto-paid (0 = no milestone). */
  streakBonus: number;
  /** Current streak length after this game. */
  streakCount: number;
  /** True when a banked saver just absorbed a missed day. */
  saverConsumed: boolean;
  /** True when this game banked a new saver. */
  saverEarned: boolean;
}

// ─── Quest pool ───────────────────────────────────────────────────────────────

export const QUEST_POOL: QuestDef[] = [
  { id: "win_any",    label: "Win a game",                    target: 1,  reward: 20,
    progressFrom: (r) => (r.won ? 1 : 0) },
  { id: "win_2",      label: "Win 2 games",                   target: 2,  reward: 25,
    progressFrom: (r) => (r.won ? 1 : 0) },
  { id: "win_medium", label: "Win a Medium game",             target: 1,  reward: 30,
    progressFrom: (r) => (r.won && r.difficultyId === "medium" ? 1 : 0) },
  { id: "win_expert", label: "Win an Expert game",            target: 1,  reward: 45,
    progressFrom: (r) => (r.won && r.difficultyId === "hard" ? 1 : 0) },
  { id: "flag_15",    label: "Flag 15 mines correctly",       target: 15, reward: 20,
    progressFrom: (r) => r.correctFlags },
  { id: "flag_30",    label: "Flag 30 mines correctly",       target: 30, reward: 30,
    progressFrom: (r) => r.correctFlags },
  { id: "speed_90",   label: "Win any game in under 90s",     target: 1,  reward: 40,
    progressFrom: (r) => (r.won && r.timeSecs < 90 ? 1 : 0) },
  { id: "score_400",  label: "Score 400+ in a single game",   target: 1,  reward: 30,
    progressFrom: (r) => (r.score >= 400 ? 1 : 0) },
  { id: "play_3",     label: "Finish 3 games",                target: 3,  reward: 20,
    progressFrom: () => 1 },
  { id: "daily_tier", label: "Complete a Daily Challenge tier", target: 1, reward: 35,
    progressFrom: (r) => (r.isDaily ? 1 : 0) },
];

const QUEST_COUNT = 3;
const MAX_SAVERS = 2;
const KEY = "gridcrash.quests.v1";

// ─── Storage ──────────────────────────────────────────────────────────────────

let memState: QuestsState | null = null;

function todayUTC(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The 3 quest defs for a given UTC date — identical for every player. */
export function questsForDate(date: string): QuestDef[] {
  // XOR distances the stream from the daily-board seed for the same date.
  const rng = seededRng((getDailySeed(date, "easy") ^ 0x51e575) >>> 0);
  const indexes = QUEST_POOL.map((_, i) => i);
  for (let i = indexes.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [indexes[i], indexes[j]] = [indexes[j], indexes[i]];
  }
  return indexes.slice(0, QUEST_COUNT).map((i) => QUEST_POOL[i]);
}

function freshDay(date: string, streak: StreakState): QuestsState {
  return {
    date,
    quests: questsForDate(date).map((q) => ({ id: q.id, progress: 0, claimed: false })),
    streak,
  };
}

function readState(): QuestsState {
  const today = todayUTC();
  if (memState && memState.date === today) return memState;

  let stored: QuestsState | null = memState;
  if (!stored) {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as QuestsState;
        if (Array.isArray(parsed.quests) && parsed.streak) stored = parsed;
      }
    } catch { /* corrupt — start fresh */ }
  }

  if (stored && stored.date === today) {
    memState = stored;
    return stored;
  }

  const state = freshDay(today, stored?.streak ?? { count: 0, lastPlayedDate: null, savers: 0 });
  writeState(state);
  return state;
}

function writeState(state: QuestsState): void {
  memState = state;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* memory fallback holds */ }
}

// ─── Streak helpers ───────────────────────────────────────────────────────────

function daysBetween(fromISO: string, toISO: string): number {
  const from = Date.parse(`${fromISO}T00:00:00Z`);
  const to = Date.parse(`${toISO}T00:00:00Z`);
  return Math.round((to - from) / 86_400_000);
}

/** Milestone payout when the streak reaches `count` days, 0 otherwise. */
function milestoneBonus(count: number): number {
  if (count === 3) return 25;
  if (count === 7) return 75;
  if (count === 14) return 150;
  if (count > 14 && count % 7 === 0) return 100;
  return 0;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Today's quest defs paired with the player's progress. */
export function getTodaysQuests(): Array<{ def: QuestDef; progress: QuestProgress }> {
  const state = readState();
  return state.quests.flatMap((p) => {
    const def = QUEST_POOL.find((q) => q.id === p.id);
    return def ? [{ def, progress: { ...p } }] : [];
  });
}

export function getStreak(): StreakState {
  return { ...readState().streak };
}

/** Count of completed-but-unclaimed quests (drives the HUD chip badge). */
export function getClaimableCount(): number {
  return getTodaysQuests().filter(
    ({ def, progress }) => !progress.claimed && progress.progress >= def.target
  ).length;
}

export function getCompletedCount(): number {
  return getTodaysQuests().filter(
    ({ def, progress }) => progress.progress >= def.target
  ).length;
}

/**
 * Feed one finished game into quests + streak. Call from every
 * game-end path (normal, daily, versus). Streak milestone coins are
 * credited here; quest rewards wait for an explicit claim.
 */
export function recordGameForQuests(result: QuestGameResult): QuestUpdate {
  const state = readState();
  const today = state.date;

  // ── Quest progress ──
  const justCompleted: QuestDef[] = [];
  for (const p of state.quests) {
    const def = QUEST_POOL.find((q) => q.id === p.id);
    if (!def || p.progress >= def.target) continue;
    const gained = def.progressFrom(result);
    if (gained <= 0) continue;
    p.progress = Math.min(def.target, p.progress + gained);
    if (p.progress >= def.target) justCompleted.push(def);
  }

  // ── Streak ──
  let streakBonus = 0;
  let saverConsumed = false;
  let saverEarned = false;
  const streak = state.streak;

  if (streak.lastPlayedDate !== today) {
    const gap = streak.lastPlayedDate ? daysBetween(streak.lastPlayedDate, today) : Infinity;

    if (gap === 1) {
      streak.count += 1;
    } else if (gap === 2 && streak.savers > 0) {
      streak.savers -= 1;
      saverConsumed = true;
      streak.count += 1;
    } else {
      streak.count = 1;
    }
    streak.lastPlayedDate = today;

    streakBonus = milestoneBonus(streak.count);
    if (streakBonus > 0) creditBonus(streakBonus);

    if (streak.count > 0 && streak.count % 7 === 0 && streak.savers < MAX_SAVERS) {
      streak.savers += 1;
      saverEarned = true;
    }
  }

  writeState(state);
  return {
    justCompleted,
    streakBonus,
    streakCount: streak.count,
    saverConsumed,
    saverEarned,
  };
}

/** Pays out a completed quest. False when not completed or already claimed. */
export function claimQuest(questId: string): boolean {
  const state = readState();
  const p = state.quests.find((q) => q.id === questId);
  const def = QUEST_POOL.find((q) => q.id === questId);
  if (!p || !def || p.claimed || p.progress < def.target) return false;
  p.claimed = true;
  writeState(state);
  creditBonus(def.reward);
  return true;
}
